import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { formatNumberGroupingSeparators } from '@vmos2/core';
import { Button, CircularProgressBar, Dropdown, Text, useComponentTheme } from '@vmos2/design-system';
import { LoyaltyLogo } from '@vmos2/loyalty';
import { useLoyaltyContext, useSettings } from '@vmos2/providers';
import { ClickOutside } from '@vmos2/utils';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RewardsPopupContent } from './rewardsPopupContent';

type Props = {
  routeParams: { fromRoute: string, fromParams: string },
};

export const Rewards = ({ routeParams }: Props) => {
  const componentTheme = useComponentTheme(theme => theme.components.Rewards);
  const { loyaltyStampCard } = useSettings();
  const [rewardsDropdownVisible, setRewardsDropdownVisible] = useState(false);
  const theme = useTheme();
  const { userPoints } = useLoyaltyContext('Points');
  const { t } = useTranslation('');

  if (!userPoints.enabled || !componentTheme.visible) {
    return null;
  }

  const userRewardPoints = userPoints.points || 0;
  const pointsPerPromo = userPoints.spend?.pointsPerPromo || 0;
  const requiredPoints = loyaltyStampCard?.enabled ? loyaltyStampCard.stampsPerPromo : pointsPerPromo;
  const pointsUntilReward = requiredPoints - (userRewardPoints % requiredPoints);
  const progressPercentage = 100 - (pointsUntilReward / requiredPoints) * 100;
  const withProgressBar = !!pointsPerPromo;

  return (
    <ClickOutside onClickOutside={() => setRewardsDropdownVisible(false)}>
      <RewardsContainer>
        <RewardsBtn
          color="dark"
          data-test="rewards-btn"
          hasDropdownArrow={rewardsDropdownVisible}
          iconLeft={
            <CircularProgressBar
              logo={<LoyaltyLogo mode="HeaderLoggedIn" />}
              percentage={progressPercentage}
              size={withProgressBar ? 32 : 16}
              withProgressBar={withProgressBar}
            />
          }
          mode="ghost"
          onClick={() => setRewardsDropdownVisible(value => !value)}
          size="small"
          withProgressBar={withProgressBar}
        >
          {!!userPoints.slug && (
            <HeaderSlug margin="none" mr={1} level={4} dataTest="loyalty-slug">
              {t('common:point', { count: userPoints.points })}:
            </HeaderSlug>
          )}

          <span data-test="loyalty-balance">{formatNumberGroupingSeparators(userRewardPoints)}</span>
        </RewardsBtn>

        {rewardsDropdownVisible && (
          <Dropdown
            data-test="loyalty-dropdown"
            leftOffset
            maxHeight={`min(650px, calc(100vh - ${theme.constants.headerHeight}))`}
            minWidth={327}
          >
            <RewardsPopupContent
              loyaltyData={userPoints}
              numberUntilReward={pointsUntilReward}
              progressPercentage={progressPercentage}
              routeParams={routeParams}
              withProgressBar={withProgressBar}
            />
          </Dropdown>
        )}
      </RewardsContainer>
    </ClickOutside>
  );
};

const RewardsContainer = styled('div')`
  @media (${({ theme }) => theme.breakpoint.tablet}) {
    position: relative;
  }
`;

const RewardsBtn = styled(Button)`
  position: relative;
  ${({ withProgressBar }) =>
    withProgressBar &&
    `
    padding-top: 2px;
    padding-bottom: 2px;
  `}

  :focus {
    box-shadow: none;
  }

  & span:first-of-type {
    margin-right: ${({ theme }) => theme.margins.xs};
  }

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    position: inherit;
  }
`;

const HeaderSlug = styled(Text)`
  display: none;
  cursor: inherit;
  font-weight: ${({ theme }) => theme.fontWeight.bold};

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    display: inline-block;
  }
`;
